/**
 * FAQ Modal Component
 * Displays frequently asked questions with expandable/collapsible accordion
 */
import { X, ChevronDown, ChevronUp } from 'lucide-react';
import { useState, useEffect } from 'react';
import { trackModalView } from '../utils/visitorTracking';

interface FAQModalProps {
  open: boolean;
  onClose: () => void;
}

const faqs = [
  {
    id: 1,
    question: "What is Rab Ki Jodi Bina Dowry?",
    answer: `Rab Ki Jodi Bina Dowry (RJBD) is India's first matrimony platform created with a clear mission: to promote dowry-free, dignified, and value-based marriages.

We believe marriage is a lifelong bond — not a financial transaction.`
  },
  {
    id: 2,
    question: "Is RJBD really 100% dowry-free?",
    answer: `Yes. Absolutely.

All members joining RJBD agree to:

❌ No dowry demands
❌ No financial expectations
✔ Respectful communication only

Any violation leads to strict action or permanent ban.`
  },
  {
    id: 3,
    question: "Who can register on RJBD?",
    answer: `RJBD is open to:

• Individuals seeking dowry-free marriage
• Parents looking for dignified alliances
• Families who value simplicity, faith, and respect

Both youth and parents are welcome.`
  },
  {
    id: 4,
    question: "Is registration free?",
    answer: `✅ Yes. Registration is completely free.

Our goal is social reform, not profit.
There are no hidden charges.`
  },
  {
    id: 5,
    question: "How does RJBD ensure profile authenticity?",
    answer: `We follow multiple trust steps:

• Mandatory profile details
• Photo verification
• Manual moderation
• Community reporting system

Fake or inappropriate profiles are removed immediately.`
  },
  {
    id: 6,
    question: "Can parents manage profiles?",
    answer: `Yes.
Parents or guardians can register and manage profiles on behalf of their children with full transparency.`
  },
  {
    id: 7,
    question: "What if someone asks for dowry on RJBD?",
    answer: `Dowry-related discussion is strictly prohibited.

You can:
• Report the profile instantly
• Block the user

Our team will take immediate action

RJBD has zero tolerance for dowry.`
  },
  {
    id: 8,
    question: "Is RJBD religion-specific?",
    answer: `RJBD is faith-respecting and value-driven.

While inspired by moral and ethical values, we welcome anyone who believes in dowry-free marriages and dignity.`
  },
  {
    id: 9,
    question: "Is my data safe on RJBD?",
    answer: `Yes. Your privacy is our priority.

• Secure servers
• Data protection measures
• No sharing with third parties

Your information remains confidential and protected.`
  },
  {
    id: 10,
    question: "How can I support this movement?",
    answer: `You can support RJBD by:

• Choosing a dowry-free marriage
• Inviting friends & family
• Sharing our message on social media
• Leading by example

Every marriage without dowry is a social victory.`
  }
];

export default function FAQModal({ open, onClose }: FAQModalProps) {
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // Track modal view when opened
  useEffect(() => {
    if (open) {
      trackModalView('FAQ Modal');
    }
  }, [open]);

  if (!open) return null;

  const toggleQuestion = (id: number) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with Gradient */}
        <div className="relative bg-gradient-to-r from-rose-500 via-pink-500 to-purple-500 text-white">
          {/* Transparent Pattern Background */}
          <div 
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
            }}
          />
          
          <div className="relative px-8 py-6 text-center">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center transition-all duration-200"
            >
              <X className="w-5 h-5 text-white" />
            </button>
            
            <div className="inline-flex items-center justify-center w-16 h-16 mb-3 rounded-full bg-white/20 backdrop-blur-sm">
              <span className="text-4xl">❓</span>
            </div>
            <h2 className="text-3xl font-bold mb-2">Frequently Asked Questions</h2>
            <p className="text-rose-100 text-lg">Everything you need to know about RJBD</p>
          </div>
        </div>

        {/* FAQ Content with Scroll */}
        <div className="overflow-y-auto max-h-[calc(90vh-180px)] p-8">
          <div className="space-y-4">
            {faqs.map((faq) => (
              <div 
                key={faq.id}
                className="border border-gray-200 rounded-xl overflow-hidden transition-all duration-300 hover:shadow-md"
              >
                {/* Question Header */}
                <button
                  onClick={() => toggleQuestion(faq.id)}
                  className="w-full px-6 py-4 flex items-start justify-between bg-gradient-to-r from-rose-50 to-pink-50 hover:from-rose-100 hover:to-pink-100 transition-all duration-200"
                >
                  <div className="flex items-start space-x-4 text-left flex-1">
                    <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-rose-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                      {faq.id}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-800">{faq.question}</h3>
                    </div>
                  </div>
                  <div className="flex-shrink-0 ml-4">
                    {expandedId === faq.id ? (
                      <ChevronUp className="w-5 h-5 text-rose-500" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Answer Content with Smooth Animation */}
                <div 
                  className={`overflow-hidden transition-all duration-300 ${
                    expandedId === faq.id ? 'max-h-96' : 'max-h-0'
                  }`}
                >
                  <div className="px-6 py-4 bg-white border-t border-gray-100">
                    <div className="ml-12 text-gray-700 leading-relaxed whitespace-pre-line">
                      {faq.answer}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom Message */}
          <div className="mt-8 p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200">
            <div className="text-center">
              <p className="text-purple-800 font-semibold text-lg mb-2">💕 Still have questions?</p>
              <p className="text-purple-700">
                Feel free to reach out to our support team anytime!
              </p>
              <p className="text-purple-600 text-sm mt-2">
                📧 rjbdmat@gmail.com | 📞 +91 
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
